import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import PreFlightChecklist from './preflight/PreFlightChecklist';
import { isLocalTestSimulationEnabled } from './preflight/localTestSimulation';
import { getAircraftWbConstants } from './aircraftWbConstantsStore';
import FlightSummaryCard from './preflight/FlightSummaryCard';
import PreFlightHeader from './preflight/PreFlightHeader';
import { fetchAircraftDocuments } from './admin/services/aircraftDocumentsService';
import { getAircraftDocumentStatus } from './preflight/documentStatus';
import {
  getBookingUserIdCandidates,
  selectPreFlightBooking,
} from './booking/activeFlightSelection';
import { bookingUpdateContainsId } from './booking/bookingUtils';
import './pilotPagesMobile.css';


const cardStyle = {
  background: '#181a2a',
  borderRadius: 16,
  padding: '2.5rem 2rem',
  color: 'white',
  boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
};

const defaultWeatherIcao = 'EGTR';

function isMissingAircraftWbConstantsColumnError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('wb_constants') || message.includes('column');
}

function isMissingBookingEndColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('schema cache') || message.includes('column') || message.includes('end_date') || message.includes('end_time');
}

function isBookingsTimeOrderError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'PGRST100'
    || message.includes('failed to parse order')
    || (message.includes('order') && message.includes('time'));
}

async function runBookingsQueryWithTimeOrderFallback(buildQuery) {
  let result = await buildQuery(true);
  if (!result.error || !isBookingsTimeOrderError(result.error)) {
    return result;
  }

  result = await buildQuery(false);
  return result;
}

function parseBookingStart(booking) {
  const dateValue = String(booking?.date || '').trim();
  const timeValue = String(booking?.time || '').trim() || '00:00';

  if (!dateValue) return null;

  const parsed = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseBookingEnd(booking) {
  const start = parseBookingStart(booking);
  if (!start) return null;

  const endDateValue = String(booking?.end_date || '').trim();
  const endTimeValue = String(booking?.end_time || '').trim();

  if (endDateValue) {
    const fallbackClock = String(booking?.time || '').trim() || '23:59';
    const parsedExplicitEnd = new Date(`${endDateValue}T${endTimeValue || fallbackClock}`);
    if (!Number.isNaN(parsedExplicitEnd.getTime()) && parsedExplicitEnd > start) {
      return parsedExplicitEnd;
    }
  }

  const durationMinutes = Number(booking?.duration);
  if (Number.isFinite(durationMinutes) && durationMinutes > 0) {
    return new Date(start.getTime() + durationMinutes * 60000);
  }

  return new Date(start.getTime() + 60 * 60000);
}

function formatBookingLabel(booking) {
  const start = parseBookingStart(booking);
  const end = parseBookingEnd(booking);
  if (!start || !end) return 'No upcoming bookings';

  const startLabel = start.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  const sameDay = start.toDateString() === end.toDateString();

  if (sameDay) {
    const endTimeLabel = end.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${startLabel} - ${endTimeLabel}`;
  }

  const endLabel = end.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${startLabel} to ${endLabel}`;
}

function isMultiDayBooking(booking) {
  const start = parseBookingStart(booking);
  const end = parseBookingEnd(booking);
  if (!start || !end) return false;
  return start.toDateString() !== end.toDateString();
}

function getReturnLegRecord(booking) {
  const record = booking?.briefing_data?.return_leg_preflight;
  return record && typeof record === 'object' ? record : null;
}

function getReturnLegStatus(booking) {
  if (!booking || !isMultiDayBooking(booking)) return 'not_required';
  const record = getReturnLegRecord(booking);
  if (record?.completed) return 'complete';
  if (record?.status === 'pending') return 'pending';
  return 'required';
}

function getReturnLegStatusMeta(status) {
  if (status === 'complete') {
    return {
      label: 'COMPLETE',
      fill: '#8cff8c',
      border: '#2f7d4d',
      background: 'rgba(47, 125, 77, 0.18)',
    };
  }

  if (status === 'pending') {
    return {
      label: 'PENDING',
      fill: '#ffd27a',
      border: '#b88b2a',
      background: 'rgba(184, 139, 42, 0.18)',
    };
  }

  if (status === 'required') {
    return {
      label: 'REQUIRED',
      fill: '#ff9c9c',
      border: '#c65b5b',
      background: 'rgba(198, 91, 91, 0.18)',
    };
  }

  return {
    label: 'NOT REQUIRED',
    fill: '#b6eaff',
    border: '#4d79a8',
    background: 'rgba(77, 121, 168, 0.16)',
  };
}

export default function PreFlightPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const user = session?.user;
  const isLocalTestSimulation =
    isLocalTestSimulationEnabled(
      searchParams,
      process.env.NODE_ENV
    );
  const organisationId = String(
    session?.profile?.organisation_id ||
    user?.organisation_id ||
    user?.user_metadata?.organisation_id ||
    user?.app_metadata?.organisation_id ||
    ''
  ).trim();
  const requestedBookingId = String(
    searchParams.get('bookingId') || ''
  ).trim();
  

  const [aircraftList, setAircraftList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedAircraftId, setSelectedAircraftId] = useState('');
  const [weatherIcao, setWeatherIcao] = useState(defaultWeatherIcao);
  const [aircraftDocumentStatus, setAircraftDocumentStatus] =
  useState({
    overall: 'loading',
    loading: true,
    messages: [],
  });
  
  
  
  
  const [returnLegSaving, setReturnLegSaving] = useState(false);
  const [returnLegNotice, setReturnLegNotice] = useState('');
  const [returnLegError, setReturnLegError] = useState('');
const [preFlightState, setPreFlightState] = useState({
  planningComplete: false,
  weatherReviewed: false,
  notamsReviewed: false,
  routePlanned: false,
  weightBalanceComplete: false,
});
  const cleanIcao = String(weatherIcao || defaultWeatherIcao).trim().toUpperCase();

  const selectedAircraft = useMemo(
    () => aircraftList.find((aircraft) => String(aircraft.id) === String(selectedAircraftId)) || null,
    [aircraftList, selectedAircraftId]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadSelectedAircraftDocuments() {
      const aircraftId = selectedAircraft?.id;

      if (!aircraftId) {
        if (!cancelled) {
          setAircraftDocumentStatus({
            overall: 'loading',
            loading: false,
            messages: [],
            documentRows: [],
            aircraftId: null,
          });
        }

        return;
      }

      if (!cancelled) {
        setAircraftDocumentStatus((previous) => ({
          ...previous,
          loading: true,
          aircraftId,
        }));
      }

      try {
        const rows = await fetchAircraftDocuments(aircraftId);
        const status = getAircraftDocumentStatus(rows || []);

        if (!cancelled) {
          setAircraftDocumentStatus({
            ...status,
            loading: false,
            aircraftId,
          });
        }
      } catch (_error) {
        if (!cancelled) {
          setAircraftDocumentStatus({
            overall: 'problem',
            loading: false,
            messages: [
              {
                key: 'load_error',
                level: 'problem',
                message:
                  'Could not load aircraft documents.',
              },
            ],
            documentRows: [],
            aircraftId,
          });
        }
      }
    }

    loadSelectedAircraftDocuments();

    return () => {
      cancelled = true;
    };
  }, [selectedAircraft?.id]);

  useEffect(() => {
    async function fetchClubWeatherIcao() {
      const fallbackWeatherIcao = String(
        session?.profile?.home_airfield ||
        user?.user_metadata?.home_airfield ||
        defaultWeatherIcao
      ).trim().toUpperCase();

      if (!organisationId) {
        setWeatherIcao(fallbackWeatherIcao || defaultWeatherIcao);
        return;
      }

      const { data } = await supabase
        .from('airfields')
        .select('icao, weather_icao, organisation_id')
        .eq('organisation_id', organisationId)
        .maybeSingle();

      if (data?.weather_icao || data?.icao) {
        setWeatherIcao(
          String(data.weather_icao || data.icao)
            .trim()
            .toUpperCase()
        );
        return;
      }

      setWeatherIcao(fallbackWeatherIcao || defaultWeatherIcao);
    }

    fetchClubWeatherIcao();
  }, [organisationId, session?.profile?.home_airfield, user?.user_metadata?.home_airfield]);

  useEffect(() => {
  async function fetchAircraft() {
    if (!organisationId) {
      setAircraftList([]);
      return;
    }

    let { data, error } = await supabase
      .from('aircraft')
      .select('id, registration, icao_type, wb_constants, status')
      .eq('organisation_id', organisationId)
      .order('registration', { ascending: true });

    if (error && isMissingAircraftWbConstantsColumnError(error)) {
      const fallback = await supabase
        .from('aircraft')
        .select('id, registration, icao_type, status')
        .eq('organisation_id', organisationId)
        .order('registration', { ascending: true });

      data = (fallback.data || []).map((row) => ({
        ...row,
        wb_constants: getAircraftWbConstants(row.id),
      }));
    }

    setAircraftList(data || []);
  }

  fetchAircraft();
}, [organisationId]);

  useEffect(() => {
    async function fetchBookings() {
      if (!user?.id) {
        setBookings([]);
        return;
      }

      const bookingUserIdCandidates = await getBookingUserIdCandidates(
        supabase,
        user.id
      );

      if (bookingUserIdCandidates.length === 0) {
        setBookings([]);
        return;
      }

      let { data, error } = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
        let query = supabase
          .from('bookings')
          .select('id, user_id, aircraft_id, date, time, duration, end_date, end_time, briefing_data, briefing_updated_at, tech_log_status')
          .in('user_id', bookingUserIdCandidates)
          .order('date', { ascending: true });

        if (includeTimeOrder) {
          query = query.order('time', { ascending: true });
        }

        return query;
      });

      if (error && isMissingBookingEndColumnsError(error)) {
        const fallback = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
          let query = supabase
            .from('bookings')
            .select('id, user_id, aircraft_id, date, time, duration, briefing_data, briefing_updated_at, tech_log_status')
            .in('user_id', bookingUserIdCandidates)
            .order('date', { ascending: true });

          if (includeTimeOrder) {
            query = query.order('time', { ascending: true });
          }

          return query;
        });

        data = fallback.data || [];
        error = fallback.error;
      }

      if (!error) {
        setBookings(data || []);
      }
    }

    fetchBookings();
  }, [user?.id]);

  const nextBooking = useMemo(() => {
    return selectPreFlightBooking(
      bookings || [],
      requestedBookingId
    );
  }, [bookings, requestedBookingId]);

  const isCompletedReviewMode = useMemo(() => {
    if (!requestedBookingId || !nextBooking) {
      return false;
    }

    const preflight =
      nextBooking?.briefing_data?.preflight ||
      {};

    return (
      (
        preflight?.completed === true ||
        preflight?.completionConfirmed === true
      ) &&
      (
        preflight?.simulationOnly !== true ||
        isLocalTestSimulation
      )
    );
  }, [
    requestedBookingId,
    nextBooking,
    isLocalTestSimulation,
  ]);

  const hasSimulationOnlyPreflight =
    nextBooking?.briefing_data?.preflight
      ?.simulationOnly === true;

  useEffect(() => {
    if (nextBooking?.aircraft_id) {
      setSelectedAircraftId(String(nextBooking.aircraft_id));
      return;
    }

    if (!selectedAircraftId && aircraftList.length > 0) {
      setSelectedAircraftId(String(aircraftList[0].id));
    }
  }, [nextBooking?.aircraft_id, selectedAircraftId, aircraftList]);

  
    

  const upcomingBookings = useMemo(() => {
    if (!selectedAircraftId) return null;

    const now = new Date();
    const cutoff = new Date(now.getTime() + 48 * 60 * 60 * 1000);

    return bookings
      .filter((booking) => String(booking.aircraft_id) === String(selectedAircraftId))
      .map((booking) => ({
        booking,
        start: parseBookingStart(booking),
        end: parseBookingEnd(booking),
      }))
      .filter((entry) => entry.start && entry.end && entry.end >= now && entry.start <= cutoff)
      .sort((left, right) => left.start.getTime() - right.start.getTime())
      .map((entry) => entry.booking);
  }, [bookings, selectedAircraftId]);

  const nextBookingAircraft = useMemo(
    () => aircraftList.find((aircraft) => String(aircraft.id) === String(nextBooking?.aircraft_id)) || null,
    [aircraftList, nextBooking?.aircraft_id]
  );

  const isNextBookingWithin24Hours = useMemo(() => {
    if (!nextBooking) return false;
    const start = parseBookingStart(nextBooking);
    if (!start) return false;

    const now = new Date();
    const cutoff = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    return start <= cutoff;
  }, [nextBooking]);

  const activeReturnLegBooking = useMemo(() => {
    const now = new Date();

    return (upcomingBookings || []).find((booking) => {
      if (!isMultiDayBooking(booking)) return false;
      const start = parseBookingStart(booking);
      const end = parseBookingEnd(booking);
      return !!start && !!end && start <= now && end > now;
    }) || null;
  }, [upcomingBookings]);

  const hasFutureMultiDayBooking = useMemo(() => {
    const now = new Date();

    return (upcomingBookings || []).some((booking) => {
      if (!isMultiDayBooking(booking)) return false;
      const start = parseBookingStart(booking);
      return !!start && start > now;
    });
  }, [upcomingBookings]);

  const returnLegTargetBooking = useMemo(() => {
    if (activeReturnLegBooking) return activeReturnLegBooking;

    const now = new Date();

    return (upcomingBookings || []).find((booking) => {
      if (!isMultiDayBooking(booking)) return false;
      const start = parseBookingStart(booking);
      return !!start && start > now;
    }) || null;
  }, [activeReturnLegBooking, upcomingBookings]);

  const returnLegStatus = getReturnLegStatus(returnLegTargetBooking);
  const returnLegStatusMeta = getReturnLegStatusMeta(returnLegStatus);

  

  const planningStatusText = !nextBooking
  ? 'Not required'
  : !isNextBookingWithin24Hours
    ? 'Opens 24h before departure'
    : preFlightState.planningComplete
      ? '🟢 Ready'
      : '🟠 Incomplete';
  

  async function persistReturnLegPreflight(status) {
    if (!returnLegTargetBooking || !user?.id) return;

    setReturnLegSaving(true);
    setReturnLegNotice('');
    setReturnLegError('');

    const now = new Date().toISOString();

    const existingBriefingData = returnLegTargetBooking.briefing_data && typeof returnLegTargetBooking.briefing_data === 'object'
      ? returnLegTargetBooking.briefing_data
      : {};

    const existingReturnLeg = getReturnLegRecord(returnLegTargetBooking) || {};

    const returnLegRecord = {
  ...existingReturnLeg,
  required: true,
  status,
  completed: status === 'complete',
  updatedAt: now,
  completedAt: status === 'complete' ? now : (existingReturnLeg.completedAt || null),

  weatherReviewed: preFlightState.weatherReviewed,
  notamsReviewed: preFlightState.notamsReviewed,
  routePlanned: preFlightState.routePlanned,
  weightBalanceComplete: preFlightState.weightBalanceComplete,

  weatherIcao: cleanIcao,
  icao: cleanIcao,
};

    const nextBriefingData = {
      ...existingBriefingData,
      return_leg_preflight: returnLegRecord,
    };

    const { data: returnLegRows, error } = await supabase
      .from('bookings')
      .update({
        briefing_data: nextBriefingData,
        briefing_updated_at: now,
      })
      .eq('id', returnLegTargetBooking.id)
      .eq('user_id', user.id)
      .select('id');

    if (error || !bookingUpdateContainsId(returnLegRows, returnLegTargetBooking.id)) {
      setReturnLegError('Could not save return leg pre-flight status.');
      setReturnLegSaving(false);
      return;
    }

    setBookings((previous) => previous.map((booking) => (
      String(booking.id) === String(returnLegTargetBooking.id)
        ? {
          ...booking,
          briefing_data: nextBriefingData,
          briefing_updated_at: now,
        }
        : booking
    )));

    setReturnLegNotice(status === 'complete'
      ? 'Return leg pre-flight marked complete.'
      : 'Return leg pre-flight saved as pending.');

    setReturnLegSaving(false);
  }

  

  

  return (
    <div className="pilot-page pilot-pre-flight" style={{ maxWidth: 820, margin: '3rem auto', ...cardStyle }}>
      <PreFlightHeader navigate={navigate} />

      {isLocalTestSimulation ||
      hasSimulationOnlyPreflight ? (
        <div
          role="status"
          style={{
            marginBottom: '1rem',
            padding: '1rem',
            border: '2px solid #ffcc66',
            borderRadius: 10,
            color: '#ffe0a3',
            background: 'rgba(184, 139, 42, 0.18)',
          }}
        >
          <strong>LOCAL TEST ONLY — SYNTHETIC SIMULATION</strong>
          <div>
            Weather, NOTAMs, route, weight-and-balance and document
            states are software-test data only. No operational
            information has been verified, and this record does not
            authorise a flight.
          </div>
        </div>
      ) : null}

      <FlightSummaryCard
  heading="UPCOMING FLIGHT"
  nextBooking={nextBooking}
  nextBookingAircraft={nextBookingAircraft}
  bookingLabel={nextBooking ? formatBookingLabel(nextBooking) : ''}
  isMultiDay={nextBooking ? isMultiDayBooking(nextBooking) : false}
  planningStatusText={planningStatusText}
  returnLegTargetBooking={returnLegTargetBooking}
  returnLegStatus={returnLegStatus}
  returnLegStatusMeta={returnLegStatusMeta}
  activeReturnLegBooking={activeReturnLegBooking}
  hasFutureMultiDayBooking={hasFutureMultiDayBooking}
  returnLegSaving={returnLegSaving}
  returnLegNotice={returnLegNotice}
  returnLegError={returnLegError}
  planningComplete={preFlightState.planningComplete}
  onMakeBooking={() => navigate('/book-aircraft')}
  onSaveReturnPending={() => persistReturnLegPreflight('pending')}
  onMarkReturnComplete={() => persistReturnLegPreflight('complete')}
/>
      <PreFlightChecklist
  nextBooking={nextBooking}
  selectedAircraft={selectedAircraft}
  cleanIcao={cleanIcao}
  isNextBookingWithin24Hours={isNextBookingWithin24Hours}
  userId={user?.id}
        organisationId={session?.profile?.organisation_id}
  aircraftDocumentStatus={aircraftDocumentStatus}
  isLocalTestSimulation={isLocalTestSimulation}
  onStateChange={setPreFlightState}
  onCompletePreFlight={() => navigate('/dashboard')}
  isReviewMode={isCompletedReviewMode}
/>
    </div>
  );
}
